import { IExpenseRequestRow } from "../../services/API/GetExpenseRequests";

export interface NotificationDeepLinkOutcome {
  /** The expense row to open, if the caller is authorized to see it. */
  row?: IExpenseRequestRow;
  /** A safe, non-leaking message to show instead of opening anything. */
  errorMessage?: string;
}

/**
 * Resolves which expense (if any) a clicked notification should open. Prefers data already
 * loaded/authorized in the current view; only calls out to the network when the referenced
 * expense isn't present there (e.g. it aged out of the notificationMode result set). The actual
 * authorization decision always happens server-side in `fetchById` - this function never assumes
 * access and never fabricates a row from the notification payload alone.
 */
export async function resolveNotificationDeepLink(
  data: IExpenseRequestRow[] | undefined,
  expenseRequestId: number,
  fetchById: (
    requestId: number,
  ) => Promise<IExpenseRequestRow | null | undefined>,
): Promise<NotificationDeepLinkOutcome> {
  const matched = data?.find(
    (request) => request.expenseRequestID === expenseRequestId,
  );
  if (matched) {
    return { row: matched };
  }

  const fetched = await fetchById(expenseRequestId);
  if (fetched) {
    return { row: fetched };
  }
  if (fetched === null) {
    // Server confirmed: doesn't exist, or no longer accessible to this employee. These two
    // cases are intentionally indistinguishable to the caller (IDOR-safe).
    return { errorMessage: "This expense is no longer available to you." };
  }
  // Unrelated/unexpected error (e.g. session expiry) - already handled by the fetch itself.
  return {};
}
